import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, Users, Search, Plus, Phone, Mail, Clock, Tag,
  ChevronDown, ChevronUp, MessageSquare, FileText, X, User, Edit2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  lead: 'bg-purple-100 text-purple-700',
  active: 'bg-green-100 text-green-700',
  vip: 'bg-amber-100 text-amber-700',
  inactive: 'bg-gray-100 text-gray-500',
};

const AdminCRM = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addForm, setAddForm] = useState({ name: '', email: '', phone: '', status: 'lead', source: 'manual', notes: '' });

  const loadClients = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (statusFilter) params.set('status', statusFilter);
    try {
      const [clientsRes, dashRes] = await Promise.all([
        fetch(`${API}/api/consulting/crm/clients?${params}`),
        fetch(`${API}/api/consulting/crm/dashboard`),
      ]);
      const cd = await clientsRes.json();
      const dd = await dashRes.json();
      setClients(cd.clients || []);
      setDashboard(dd);
    } catch {}
    setLoading(false);
  }, [searchTerm, statusFilter]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const openDetail = async (clientId) => {
    setSelectedClient(clientId);
    try {
      const res = await fetch(`${API}/api/consulting/crm/clients/${clientId}`);
      setClientDetail(await res.json());
    } catch {}
  };

  const handleAddClient = async () => {
    if (!addForm.name.trim()) return;
    try {
      const res = await fetch(`${API}/api/consulting/crm/clients`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addForm),
      });
      if (res.ok) { setShowAddForm(false); setAddForm({ name: '', email: '', phone: '', status: 'lead', source: 'manual', notes: '' }); loadClients(); }
    } catch {}
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedClient) return;
    try {
      await fetch(`${API}/api/consulting/crm/clients/${selectedClient}/note`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: newNote, type: 'note' }),
      });
      setNewNote('');
      openDetail(selectedClient);
    } catch {}
  };

  const handleUpdateStatus = async (clientId, status) => {
    try {
      await fetch(`${API}/api/consulting/crm/clients/${clientId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      loadClients();
      if (selectedClient === clientId) openDetail(clientId);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-crm-page">
      {/* Header */}
      <nav className="bg-black text-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-gray-400 hover:text-white"><ArrowLeft className="h-5 w-5" /></Link>
            <Users className="h-5 w-5 text-red-500" />
            <span className="font-bold text-sm">Client Profiles (CRM)</span>
          </div>
          <Button onClick={() => setShowAddForm(true)} size="sm" className="bg-red-600 hover:bg-red-700 text-white gap-1 text-xs" data-testid="add-client-btn">
            <Plus className="h-3.5 w-3.5" /> Add Client
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Dashboard Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Card className="p-4 text-center" data-testid="stat-total">
              <p className="text-2xl font-bold">{dashboard.total_clients}</p>
              <p className="text-xs text-gray-500">Total Clients</p>
            </Card>
            {Object.entries(dashboard.by_status || {}).slice(0, 3).map(([status, count]) => (
              <Card key={status} className="p-4 text-center" data-testid={`stat-${status}`}>
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs text-gray-500 capitalize">{status}</p>
              </Card>
            ))}
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" placeholder="Search clients..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm"
              data-testid="crm-search" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="crm-status-filter">
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="lead">Lead</option>
            <option value="active">Active</option>
            <option value="vip">VIP</option>
          </select>
        </div>

        {/* Add Form Modal */}
        {showAddForm && (
          <Card className="mb-6 p-5 border-2 border-red-200" data-testid="add-client-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">Add New Client</h3>
              <button onClick={() => setShowAddForm(false)}><X className="h-4 w-4 text-gray-400" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <input type="text" placeholder="Full Name *" value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="add-name" />
              <input type="email" placeholder="Email" value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="add-email" />
              <input type="tel" placeholder="Phone" value={addForm.phone} onChange={e => setAddForm(p => ({ ...p, phone: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="add-phone" />
              <select value={addForm.status} onChange={e => setAddForm(p => ({ ...p, status: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="add-status">
                <option value="lead">Lead</option>
                <option value="new">New</option>
                <option value="active">Active</option>
                <option value="vip">VIP</option>
              </select>
            </div>
            <textarea placeholder="Notes (optional)" value={addForm.notes} onChange={e => setAddForm(p => ({ ...p, notes: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3" rows="2" data-testid="add-notes" />
            <Button onClick={handleAddClient} className="bg-red-600 hover:bg-red-700 text-white text-xs" data-testid="submit-add-client">Add Client</Button>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Client List */}
          <div className="lg:col-span-2 space-y-2">
            {loading ? (
              <div className="text-center py-12"><div className="animate-spin h-8 w-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto" /></div>
            ) : clients.length === 0 ? (
              <Card className="p-12 text-center"><Users className="h-10 w-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">No clients found. Add your first client or collect leads from forms.</p></Card>
            ) : (
              clients.map(c => (
                <Card key={c.client_id} className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${selectedClient === c.client_id ? 'ring-2 ring-red-500' : ''}`}
                  onClick={() => openDetail(c.client_id)} data-testid={`client-${c.client_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                        <User className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-sm truncate">{c.name}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          {c.email && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{c.email}</span>}
                          {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[9px] font-bold uppercase px-2 py-1 rounded-full ${STATUS_COLORS[c.status] || STATUS_COLORS.new}`}>
                        {c.status || 'new'}
                      </span>
                      <span className="text-[9px] text-gray-400">{c.source}</span>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>

          {/* Detail Panel */}
          <div>
            {clientDetail ? (
              <Card className="p-5 sticky top-20" data-testid="client-detail-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-sm">Client Profile</h3>
                  <button onClick={() => { setSelectedClient(null); setClientDetail(null); }}>
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                </div>

                <div className="text-center mb-4">
                  <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                    <User className="h-7 w-7 text-gray-500" />
                  </div>
                  <p className="font-bold">{clientDetail.client?.name}</p>
                  <span className={`text-[9px] font-bold uppercase px-2 py-1 rounded-full ${STATUS_COLORS[clientDetail.client?.status] || STATUS_COLORS.new}`}>
                    {clientDetail.client?.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  {clientDetail.client?.email && <p className="flex items-center gap-2 text-gray-600"><Mail className="h-3.5 w-3.5 text-gray-400" />{clientDetail.client.email}</p>}
                  {clientDetail.client?.phone && <p className="flex items-center gap-2 text-gray-600"><Phone className="h-3.5 w-3.5 text-gray-400" />{clientDetail.client.phone}</p>}
                  <p className="flex items-center gap-2 text-gray-600"><Tag className="h-3.5 w-3.5 text-gray-400" />Source: {clientDetail.client?.source}</p>
                  <p className="flex items-center gap-2 text-gray-600"><Clock className="h-3.5 w-3.5 text-gray-400" />{new Date(clientDetail.client?.created_at).toLocaleDateString()}</p>
                </div>

                {/* Status Controls */}
                <div className="mb-4">
                  <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Status</p>
                  <div className="flex flex-wrap gap-1">
                    {['new', 'lead', 'active', 'vip'].map(s => (
                      <button key={s} onClick={() => handleUpdateStatus(selectedClient, s)}
                        className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${clientDetail.client?.status === s ? STATUS_COLORS[s] : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                        data-testid={`set-status-${s}`}>{s}</button>
                    ))}
                  </div>
                </div>

                {/* Form Submissions */}
                {(clientDetail.intakes?.length > 0 || clientDetail.assessments?.length > 0) && (
                  <div className="mb-4">
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Form Submissions</p>
                    <div className="space-y-1.5">
                      {(clientDetail.intakes || []).map((intake, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs p-2 bg-blue-50 rounded-lg">
                          <FileText className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{intake.form_type === 'client_profile' ? 'Consulting Intake' : intake.form_type}</p>
                            <p className="text-[10px] text-gray-500">Route: {intake.route || 'N/A'} | Stage: {intake.stage}</p>
                          </div>
                        </div>
                      ))}
                      {(clientDetail.assessments || []).map((a, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs p-2 bg-purple-50 rounded-lg">
                          <FileText className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">Lifestyle Assessment</p>
                            <p className="text-[10px] text-gray-500">Persona: {a.persona || 'N/A'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Notes & Activity</p>
                  <div className="flex gap-2 mb-3">
                    <input type="text" placeholder="Add a note..." value={newNote} onChange={e => setNewNote(e.target.value)}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs" data-testid="add-note-input"
                      onKeyDown={e => e.key === 'Enter' && handleAddNote()} />
                    <Button onClick={handleAddNote} size="sm" className="bg-gray-800 hover:bg-black text-white text-xs px-3" data-testid="add-note-btn">Add</Button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(clientDetail.communications || []).map((c, i) => (
                      <div key={i} className="text-xs p-2 bg-gray-50 rounded-lg">
                        <p className="text-gray-700">{c.message}</p>
                        <p className="text-[9px] text-gray-400 mt-1">{new Date(c.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                    {(!clientDetail.communications || clientDetail.communications.length === 0) && (
                      <p className="text-xs text-gray-400 text-center py-2">No notes yet</p>
                    )}
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="p-8 text-center text-gray-400">
                <Users className="h-8 w-8 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Select a client to view their profile</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCRM;
